import { Button, Col, DatePicker, DatePickerProps, Form, Input, InputNumber, Row, Table, Tag, Select } from 'antd';
import Column from 'antd/es/table/Column';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { ContentHeader } from '../../components';
import ModalTextForm from '../../components/modal/ModalTextForm';
import { Links } from '../../helpers/Links';
import { dateCommon, formatVND, validateMessages } from '../../helpers/common';
import { hideLoadingScreen, showLoadingScreen } from '../../store/reducers/loading';
import { khoiTaoLuongHoanThanhNhiemVu, layDsLuongHoanThanhNhiemVuTheoNam, timKiemDsLuongHoanThanhNhiemVu } from '../../services/luong';
import { LuongHoanThanhNhiemVuInfo } from '../../models/luong/LuongHoanThanhNhiemVuModel';

const generateYears = () => {
    const years = [];
    const now = dayjs();
    const startDate = now.subtract(3, 'year').startOf('year');
    const endDate = now.add(1, 'year').endOf('year');
    
    let current = startDate;
    while (current.isBefore(endDate) || current.isSame(endDate)) {
        years.push(current.format('YYYY'));
        current = current.add(1, 'year');
    }
    return [...new Set(years)].map(year => ({ label: `Năm ${year}`, value: Number(year) }));
};

const DanhSachLuongHoanThanhNhiemVu = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [dataDs, setDataDs] = useState<LuongHoanThanhNhiemVuInfo[]>([]);
    const [ngayThang, setNgayThang] = useState<Dayjs | null>(null);
    const [openModalKhoiTao, setOpenModalKhoiTao] = useState(false);
    const [kyLuongKhoiTao, setKyLuongKhoiTao] = useState<Dayjs | null>(dayjs());
    const [dsKyLuongDaTao, setDsKyLuongDaTao] = useState<any[]>([]);

    const loaiWatch = Form.useWatch('loai', form);
    const [selectYear, setSelectYear] = useState(dayjs().year());

    const layDuLieu = async (thangNam?: string) => {
        setLoading(true);
        try {
            const rs = await timKiemDsLuongHoanThanhNhiemVu(0, 1000, thangNam);
            setDataDs(rs?.data ?? []);
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        setLoading(false);
    };

    useEffect(() => {
        layDuLieu(ngayThang ? ngayThang.format('MM/YYYY') : undefined);
    }, []);

    const monthFormat = 'MM/YYYY';
    const thangNamFormat: DatePickerProps['format'] = (value) => `Tháng ${value.format(monthFormat)}`;

    const onChangeThangNam: DatePickerProps['onChange'] = (date) => {
        setNgayThang(date);
        layDuLieu(date ? date.format('MM/YYYY') : undefined);
    };

    const onMoModalKhoiTao = async () => {
        const ky = ngayThang ?? dayjs();
        setKyLuongKhoiTao(ky);
        setSelectYear(ky.year());
        form.setFieldsValue({
            kyLuong: ky,
            nam: ky.year(),
            loai: 0,
            quyLuong: undefined,
            thangLayDuLieu: [],
            tenKyLuong: undefined
        });
        setOpenModalKhoiTao(true);
    };

    const normalizeMonthsList = (strOrArr?: string | string[]): string[] => {
        if (!strOrArr) return [];
        const list = Array.isArray(strOrArr) ? strOrArr : strOrArr.split(',');
        return list.map((m) => {
            const trimmed = m.trim();
            const parts = trimmed.split('/');
            if (parts.length === 2) {
                return `${parts[0].padStart(2, '0')}/${parts[1]}`;
            }
            return trimmed;
        }).filter(Boolean);
    };

    const onFinishKhoiTao = async (values: any) => {
        dispatch(showLoadingScreen());
        try {
            const ky = values?.kyLuong as Dayjs;
            const request = {
                thangNam: values?.loai === 0 && ky ? ky.format('MM/YYYY') : null,
                nam: values?.loai === 1 ? values?.nam : (ky ? ky.year() : null),
                tenKyLuong: values?.tenKyLuong,
                quyLuong: Number(values?.quyLuong ?? 0),
                loai: values?.loai ?? 0,
                thangLayDuLieu: values?.loai === 1 ? normalizeMonthsList(values?.thangLayDuLieu).join(',') : null,
            };
            const rs = await khoiTaoLuongHoanThanhNhiemVu(request);
            if (rs > 0) {
                toast.success('Khởi tạo lương hoàn thành nhiệm vụ thành công!');
                setOpenModalKhoiTao(false);
                layDuLieu(ngayThang ? ngayThang.format('MM/YYYY') : undefined);
            } else {
                toast.error('Khởi tạo lương hoàn thành nhiệm vụ thất bại!');
            }
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        dispatch(hideLoadingScreen());
    };

    return (
        <section>
            {openModalKhoiTao && (
                <ModalTextForm
                    title="Khởi tạo lương hoàn thành nhiệm vụ"
                    hideFooter
                    show={openModalKhoiTao}
                    handleClose={() => setOpenModalKhoiTao(false)}
                >
                    <Form
                        layout="vertical"
                        validateMessages={validateMessages}
                        onFinish={onFinishKhoiTao}
                        form={form}
                    >
                        <Row gutter={8}>
                            <Col span={12}>
                                <Form.Item rules={[{ required: true }]} label="Loại" name="loai">
                                    <Select
                                        options={[
                                            { value: 0, label: "Tạm ứng" },
                                            { value: 1, label: "Bổ sung/Quyết toán" },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            {loaiWatch !== 1 ? (
                                <Col span={12}>
                                    <Form.Item rules={[{ required: true }]} label="Tháng năm" name="kyLuong">
                                        <DatePicker
                                            changeOnBlur={true}
                                            picker="month"
                                            format={["MM/YYYY", "MMYYYY"]}
                                            placeholder="Chọn kỳ lương"
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                            ) : (
                                <Col span={12}>
                                    <Form.Item rules={[{ required: true }]} label="Năm" name="nam">
                                        <Select options={generateYears()} />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item label="Tên kỳ lương" name="tenKyLuong" rules={[{ required: loaiWatch === 1, message: 'Vui lòng nhập tên kỳ lương' }]}>
                                    <Input placeholder={loaiWatch === 1 ? "Nhập tên kỳ bổ sung/quyết toán..." : "Nhập tên kỳ lương (tuỳ chọn)"} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={12}>
                                <Form.Item rules={[{ required: true }]} label="Quỹ lương HTNV" name="quyLuong">
                                    <InputNumber
                                        min={0}
                                        precision={0}
                                        placeholder="Nhập quỹ lương"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                            {loaiWatch === 1 && (
                                <Col span={12}>
                                    <Form.Item rules={[{ required: true, message: 'Vui lòng chọn các tháng!' }]} label="Chọn tháng lấy dữ liệu" name="thangLayDuLieu">
                                        <Select
                                            mode="multiple"
                                            placeholder="Chọn các tháng"
                                            dropdownStyle={{ minWidth: 220 }}
                                            tagRender={(props) => {
                                                const { label, closable, onClose } = props;
                                                return (
                                                    <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
                                                        {label}
                                                    </Tag>
                                                );
                                            }}
                                            dropdownRender={() => {
                                                const value = normalizeMonthsList(form.getFieldValue('thangLayDuLieu') || []);
                                                return (
                                                    <div style={{ padding: 8, background: '#fff' }} onMouseDown={(e) => e.preventDefault()}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
                                                            <Button size="small" type="text" onClick={() => setSelectYear(selectYear - 1)}>&lt;</Button>
                                                            <strong>Năm {selectYear}</strong>
                                                            <Button size="small" type="text" onClick={() => setSelectYear(selectYear + 1)}>&gt;</Button>
                                                        </div>
                                                        <Row gutter={[4, 4]}>
                                                            {Array.from({ length: 12 }, (_, i) => {
                                                                const mNum = i + 1;
                                                                const mStr = `${mNum < 10 ? '0' + mNum : mNum}/${selectYear}`;
                                                                const isSelected = value.includes(mStr);
                                                                return (
                                                                    <Col span={8} key={mStr}>
                                                                        <Button
                                                                            size="small"
                                                                            type={isSelected ? "primary" : "default"}
                                                                            style={{ width: '100%', fontSize: '12px', padding: '0 4px' }}
                                                                            onClick={() => {
                                                                                const newValue = isSelected
                                                                                    ? value.filter((x: string) => x !== mStr)
                                                                                    : normalizeMonthsList([...value, mStr]).sort((a, b) => {
                                                                                        const [ma, ya] = a.split('/').map(Number);
                                                                                        const [mb, yb] = b.split('/').map(Number);
                                                                                        if (ya !== yb) return (ya || 0) - (yb || 0);
                                                                                        return (ma || 0) - (mb || 0);
                                                                                    });
                                                                                form.setFieldValue('thangLayDuLieu', newValue);
                                                                            }}
                                                                        >
                                                                            Thg {mNum}
                                                                        </Button>
                                                                    </Col>
                                                                );
                                                            })}
                                                        </Row>
                                                    </div>
                                                );
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>
                        <Form.Item>
                            <Button
                                type="primary"
                                className="float-right"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                            >
                                Khởi tạo
                            </Button>
                        </Form.Item>
                    </Form>
                </ModalTextForm>
            )}

            <ContentHeader title="Lương hoàn thành nhiệm vụ" />

            <Row className="form-group" gutter={8}>
                <Col span={6}>
                    <DatePicker
                        changeOnBlur={true}
                        value={ngayThang}
                        onChange={onChangeThangNam}
                        placeholder="Tháng tính lương"
                        picker="month"
                        format={thangNamFormat}
                        style={{ width: '100%' }}
                    />
                </Col>
                <Col span={18}>
                    <Button
                        className="hrm-btn hrm-btn-warning float-right"
                        icon={<PlusOutlined />}
                        onClick={onMoModalKhoiTao}
                    >
                        Khởi tạo lương HTNV
                    </Button>
                </Col>
            </Row>

            <Row gutter={8}>
                <Col span={24}>
                    <div className="card card-primary">
                        <Row>
                            <Col span={24}>
                                <Table
                                    locale={{ emptyText: "Không có dữ liệu" }}
                                    dataSource={(dataDs || []).map((value, index) => ({ key: index, ...value }))}
                                    loading={loading}
                                >
                                    <Column
                                        title="STT"
                                        width={60}
                                        align="center"
                                        render={(_v, _r, index) => (
                                            <div className="text-align-middle">{index + 1}</div>
                                        )}
                                    />
                                    <Column
                                        title="Kỳ lương"
                                        key="tenKyLuong"
                                        align="left"
                                        render={(value: any) => (
                                            <div className="text-align-middle">{value?.tenKyLuong}</div>
                                        )}
                                    />
                                    <Column
                                        title="Quỹ lương"
                                        key="quyLuong"
                                        align="center"
                                        render={(value: any) => (
                                            <div className="text-align-middle">
                                                {formatVND(value?.quyLuong ?? 0)}
                                            </div>
                                        )}
                                    />
                                    <Column
                                        title="Loại"
                                        key="loai"
                                        align="center"
                                        render={(value: any) => (
                                            value.loai === 1 ? (<Tag color="purple">Bổ sung/Quyết toán</Tag>) : (<Tag color="default">Tạm ứng</Tag>)
                                        )}
                                    />
                                    <Column
                                        title="Trạng thái"
                                        key="trangThai"
                                        align="center"
                                        render={(value: any) => (
                                            value.trangThai == 0 ? (<Tag color="blue">Khởi tạo</Tag>) : (<Tag color="success">Đã xác nhận</Tag>)
                                        )}
                                    />
                                    <Column
                                        title="Người khởi tạo"
                                        key="tenNVKhoiTao"
                                        align="center"
                                        render={(value) => (
                                            <div className="text-align-middle">{value?.tenNVKhoiTao}</div>
                                        )}
                                    />
                                    <Column
                                        title="Ngày khởi tạo"
                                        key="ngayKhoiTao"
                                        align="center"
                                        render={(value) => (
                                            <div className="text-align-middle">{dateCommon.formatDate(value?.ngayKhoiTao)}</div>
                                        )}
                                    />
                                    <Column
                                        title=""
                                        width={120}
                                        key="action"
                                        align="center"
                                        render={(value: any, _record, index) => (
                                            <Link
                                                key={"link" + index}
                                                className="hrm-btn hrm-btn-sm btn-primary"
                                                to={`${Links.Luong.ChiTietLuongHoanThanhNhiemVu}?id=${value?.id}`}
                                            >
                                                Xem
                                            </Link>
                                        )}
                                    />
                                </Table>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </section>
    );
};

export default DanhSachLuongHoanThanhNhiemVu;